// @ts-check
// File: app/src/engine/WorkoutEngine.js
//
// Vanilla JS state machine that owns the compiled execution queue and the
// current playback position. The engine is deliberately ignorant of
// real-world time intervals: it only knows indexes, statuses, and a
// countdown for count-in.
//
// The React layer (useWorkoutCoach) drives the engine via explicit method
// calls and reads state via getCurrentState() on every tick. No React
// imports, no side effects, no timers.

import { compileWorkoutPlan } from "../utils/workoutCompiler";

/** @typedef {import("../types/plan").PlanNode} PlanNode */
/** @typedef {import("../types/plan").QueueItem} QueueItem */
/** @typedef {"ready" | "countIn" | "readyGate" | "running" | "paused" | "finished"} EngineStatus */

const STATUS = Object.freeze({
  READY: "ready",
  COUNT_IN: "countIn",
  // Paused-but-counting-up gate entered when advancing from a rep phase into a
  // timed ACTIVE phase. The user repositions/sets up; the count-up ("faff")
  // timer shows how long they're taking. Tapping Ready (confirmReady) runs the
  // normal count-in and starts the clock.
  READY_GATE: "readyGate",
  RUNNING: "running",
  PAUSED: "paused",
  FINISHED: "finished",
});

// Hard cap on a single Ready-gate count-up. If the user walks away, the gate
// stops accruing and the session auto-pauses so `faffTotal`/telemetry and the
// remaining-time estimate don't run away.
const READY_GATE_MAX_SECONDS = 300;

// What happens at a rep-to-timed changeover. Mirrors WORKOUT_DEFAULTS
// .changeoverHold; the engine takes it per-call rather than holding it.
export const CHANGEOVER_HOLD = {
  WAIT: "wait",   // hold on the Ready gate until tapped (original behaviour)
  COUNT: "count", // no hold, just the usual count-in
  OFF: "off",     // straight into the phase
};

export class WorkoutEngine {
  /**
   * @param {PlanNode} workoutPlan  Nested workout plan tree (not mutated)
   * @param {{ skipLastRest?: boolean, profile?: Object, historyMap?: Object }} options
   *   `skipLastRest` defaults to true. `profile.training_data` feeds the rep
   *   estimator (pace, tempos, working loads); `historyMap` is per-exercise
   *   logged pace, blended with the formula once there are enough samples.
   *
   * profile/historyMap change only `durationSec` on rep-based items, never
   * which items are emitted or their order. Without them the engine ran on
   * generic formula estimates while every other surface — the session card's
   * total, remaining-time maths, the live timeline — compiled WITH them, so
   * the running session disagreed with the numbers it had just shown you.
   */
  constructor(workoutPlan, { skipLastRest = true, profile, historyMap } = {}) {
    const { queue, totalDuration } = compileWorkoutPlan(workoutPlan, {
      skipLastRest,
      profile,
      historyMap,
    });
    this.workoutPlan = workoutPlan;
    this.skipLastRest = skipLastRest;
    this.profile = profile;
    this.historyMap = historyMap;
    this.queue = queue;
    this.totalDuration = totalDuration;
    this.currentIndex = 0;
    /** @type {EngineStatus} */
    this.status = STATUS.READY;
    this.countInRemaining = 0;
    this.countInTotal = 0;
    this.readyGateElapsed = 0; // count-up within the current Ready gate
    this.faffTotal = 0; // session total time spent in Ready gates
    this._pausedFromGate = false; // restore the gate on resume after a pause
  }

  // ---------------------------------------------------------------------------
  // Live reconfiguration
  // ---------------------------------------------------------------------------

  /**
   * Rewrite fields on every queue item compiled from one plan node, from the
   * current position onward.
   *
   * The queue is compiled once at start, so a plan edit made mid-session cannot
   * reach the phase already playing — the running Coach would keep announcing
   * the old weight until the next session. This is the way in for a correction
   * that has to take effect NOW: adjusting load on the set you are standing in
   * front of (issue #76).
   *
   * Deliberately narrow:
   *   • Only items at or after `currentIndex` are touched. Sets already logged
   *     record what was actually lifted; rewriting them would falsify history.
   *   • `durationSec` is left alone. Load feeds the rep-time estimate, so
   *     recomputing it here would shift the projected total (and the ring)
   *     under the user mid-phase. The next compile picks the new estimate up.
   *   • A field whose value is `undefined` is deleted rather than set, which is
   *     how a switch between `weight` and `intensityPct` clears the one it
   *     replaces.
   *
   * @param {Array}  sourceTreePath  Compiler path identifying the plan node.
   * @param {Object} fields          Shallow patch applied to each item's node.
   * @param {{ setIdx?: number | null }} options  `setIdx` restricts to items
   *                                 from this iteration of the enclosing repeat
   *                                 Group; omit for every remaining set.
   * @returns {number} How many queue items were rewritten.
   */
  updateNodeFields(sourceTreePath, fields, { setIdx = null } = {}) {
    if (!Array.isArray(sourceTreePath) || sourceTreePath.length === 0) return 0;
    if (!fields || typeof fields !== "object") return 0;

    const samePath = (path) =>
      Array.isArray(path) &&
      path.length === sourceTreePath.length &&
      path.every((seg, i) => seg === sourceTreePath[i]);

    let patched = 0;
    for (let i = Math.max(0, this.currentIndex); i < this.queue.length; i++) {
      const item = this.queue[i];
      if (!samePath(item?.sourceTreePath)) continue;
      if (setIdx != null && item.enclosingGroup?.iter !== setIdx) continue;
      const node = { ...item.node };
      for (const [key, value] of Object.entries(fields)) {
        if (value === undefined) delete node[key];
        else node[key] = value;
      }
      item.node = node;
      patched += 1;
    }
    return patched;
  }

  // Recompile the queue with new options and preserve the user's current
  // playback position by matching sourceTreePath + loopCounters of the
  // previously active item against the freshly compiled queue.
  updateConfig({ skipLastRest }) {
    this.skipLastRest = skipLastRest;
    this._recompile();
  }

  /**
   * Swap in an edited plan tree mid-session and re-time the remaining queue.
   *
   * `updateNodeFields` is the other way in, and the two are not
   * interchangeable. That one patches node fields on the compiled queue without
   * touching `durationSec` — correct for load, which only feeds an estimate.
   * For a `value`/`unit` edit the duration IS the change, so the queue has to
   * be rebuilt or the session would keep running the old clock and only pick
   * the edit up next time (#77).
   *
   * Recompiling costs the user their position, so it is restored the same way
   * updateConfig does: by matching the active item's tree path + loop counters
   * in the fresh queue. An edit that DELETES the phase in progress has no match
   * to return to, so the index is clamped and the caller is told the phase
   * changed underneath it.
   *
   * Intended for a paused session. Nothing here stops it running mid-phase, but
   * the phase clock lives in the caller (`timeLeftInPhase`), so a live swap
   * would move the goalposts under a running countdown.
   *
   * @param {Object} nextPlan  The edited plan tree. Ignored when falsy.
   * @returns {{ reseated: boolean, activeItem: Object|null, durationChanged: boolean }}
   *          `reseated` false means the phase in progress no longer exists.
   *          `durationChanged` reports whether the active phase's own duration
   *          moved, which is what tells the caller to rebase its phase clock.
   */
  updatePlan(nextPlan) {
    if (!nextPlan) {
      return { reseated: true, activeItem: this._currentItem(), durationChanged: false };
    }
    const prevItem = this._currentItem();
    const prevDuration = prevItem?.durationSec ?? 0;

    this.workoutPlan = nextPlan;
    const reseated = this._recompile();

    const activeItem = this._currentItem();
    return {
      reseated,
      activeItem,
      durationChanged:
        reseated && (activeItem?.durationSec ?? 0) !== prevDuration,
    };
  }

  // Rebuild the queue from the current plan + options, then put the playhead
  // back on the item that was active. Shared by updateConfig and updatePlan so
  // there is one implementation of "recompile without losing the user's place".
  // Returns whether the previously active item was found again.
  _recompile() {
    const prevItem = this._currentItem();
    const prevPath = prevItem?.sourceTreePath;
    const prevCounters = prevItem?.loopCounters;

    const { queue, totalDuration } = compileWorkoutPlan(this.workoutPlan, {
      skipLastRest: this.skipLastRest,
      profile: this.profile,
      historyMap: this.historyMap,
    });
    this.queue = queue;
    this.totalDuration = totalDuration;

    if (!prevItem || queue.length === 0) {
      this.currentIndex = 0;
      return false;
    }

    const samePath = (a, b) =>
      a && b && a.length === b.length && a.every((v, i) => v === b[i]);
    const sameCounters = (a, b) => {
      if (!a || !b) return false;
      const ak = Object.keys(a);
      const bk = Object.keys(b);
      if (ak.length !== bk.length) return false;
      return ak.every((k) => a[k] === b[k]);
    };

    // Node id first, tree path second. `sourceTreePath` is POSITIONAL, so an
    // edit that deletes or inserts a sibling shifts every path after it: after
    // deleting the phase in progress, its old path resolves to whatever slid
    // into the gap and the user silently resumes on a different exercise.
    // Ids survive edits (planEditing preserves them), so they identify the
    // phase across a structural change. Loop counters disambiguate the round,
    // exactly as the compiler's own instanceId does for repeated ids.
    // The path fallback covers legacy plans whose nodes carry no id.
    const matchBy = (pred) => {
      for (let i = 0; i < queue.length; i++) {
        if (pred(queue[i])) return i;
      }
      return -1;
    };

    // These are alternatives, not a chain: when the phase HAS an id, failing to
    // find it means the phase is genuinely gone, and falling back to the path
    // would then match whatever slid into its index — resuming the user on a
    // different exercise while reporting success. Only an id-less legacy plan
    // uses the positional key.
    const prevId = prevItem.node?.id;
    const matchIdx =
      prevId == null || prevId === ""
        ? matchBy(
            (it) =>
              samePath(it.sourceTreePath, prevPath) &&
              sameCounters(it.loopCounters, prevCounters),
          )
        : matchBy(
            (it) =>
              it.node?.id === prevId &&
              sameCounters(it.loopCounters, prevCounters),
          );

    if (matchIdx >= 0) {
      this.currentIndex = matchIdx;
      return true;
    }
    // The active phase is gone (deleted, or its ancestor's round count shrank).
    // Clamp rather than reset to 0 — the nearest surviving position is a far
    // better place to resume than the top of the session.
    this.currentIndex = this._clampIndex(this.currentIndex);
    const seeked = this._seekExecutable(this.currentIndex, 1);
    if (seeked >= 0) this.currentIndex = seeked;
    return false;
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  _clampIndex(i) {
    if (this.queue.length === 0) return 0;
    return Math.max(0, Math.min(i, this.queue.length - 1));
  }

  _currentItem() {
    return this.queue[this.currentIndex] || null;
  }

  // Micro-transitions are tagged `executable:false` by the compiler — they are
  // time predictions, not phases the engine lands on. These two helpers let
  // every navigation method step over them so rep→rep is instant and the user
  // never sees a transition countdown.
  _isExecutable(i) {
    const it = this.queue[i];
    return it ? it.executable !== false : false;
  }

  // From index `i`, walk in `dir` (+1 / -1) to the first executable index.
  // Returns -1 if none exists in that direction.
  _seekExecutable(i, dir) {
    let j = i;
    while (j >= 0 && j < this.queue.length && !this._isExecutable(j)) {
      j += dir;
    }
    return j >= 0 && j < this.queue.length ? j : -1;
  }

  // Arms count-in if entering a timed ACTIVE phase via a manual action
  // (start, resume, manualSkip) OR when naturally advancing out of a
  // non-timed (rep-based) phase.
  _armCountInIfNeeded(opts, reason, fromItem = null) {
    const seconds = opts?.countInSeconds;
    const target = this._currentItem();

    // 1. Must have a valid duration
    if (!seconds || seconds <= 0) return false;

    // 2. Target must be timed
    if (!target?.isTimed) return false;

    // 3. UX Rule: Never count-in to a Rest/Recovery phase.
    // We only need a buffer to get into position for Work/Active phases.
    if (target.kind === "recovery") return false;

    // 4. If it's a natural timer-zero advance from another timed phase,
    // transition instantly. (This handles the Tabata flow).
    if (reason === "naturalAdvance" && fromItem?.isTimed) return false;

    // 5. If we reach here, it's either a manual action (start/resume/skip)
    // or an advance from a rep phase. Arm the count-in.
    this.countInRemaining = seconds;
    this.countInTotal = seconds;
    this.status = STATUS.COUNT_IN;
    return true;
  }

  // Decide what happens when we land on a new target after a manual advance.
  // When the IMMEDIATELY PRECEDING executable phase is a rep-based ACTIVE phase
  // and the target is a timed ACTIVE phase, enter the Ready gate (the user just
  // finished reps and needs to get to / set up the next timed station). After a
  // Setup or strategic Rest (both timed recovery), or for any other transition,
  // fall through to the normal count-in rules — this avoids a "double gate"
  // right after a Setup/Rest that already buffered the changeover. Returns true
  // when it took ownership of the status (gate or count-in).
  _enterGateOrArm(opts, reason, fromItem) {
    const target = this._currentItem();
    const fromRepActive =
      !!fromItem && !fromItem.isTimed && fromItem.kind !== "recovery";
    const toTimedActive = !!target?.isTimed && target.kind !== "recovery";
    if (fromRepActive && toTimedActive) {
      // How long the changeover gets is the user's call (Settings → Coach
      // Behaviour). "wait" is the original behaviour and the default: hold
      // until they say so. "count" gives them the ordinary 3-2-1 instead, and
      // "off" drops straight into the phase. Read from opts rather than engine
      // state so a change takes effect on the next transition without a
      // recompile.
      const hold = opts?.changeoverHold || CHANGEOVER_HOLD.WAIT;
      if (hold === CHANGEOVER_HOLD.OFF) return false;
      if (hold === CHANGEOVER_HOLD.COUNT) {
        return this._armCountInIfNeeded(opts, reason, fromItem);
      }
      this.status = STATUS.READY_GATE;
      this.readyGateElapsed = 0;
      this._pausedFromGate = false;
      this.countInRemaining = 0;
      this.countInTotal = 0;
      return true;
    }
    return this._armCountInIfNeeded(opts, reason, fromItem);
  }

  // ---------------------------------------------------------------------------
  // Playback control
  // ---------------------------------------------------------------------------

  start(opts) {
    if (this.queue.length === 0) {
      this.status = STATUS.FINISHED;
      return;
    }
    const first = this._seekExecutable(0, 1);
    if (first < 0) {
      this.status = STATUS.FINISHED;
      return;
    }
    this.currentIndex = first;
    if (!this._armCountInIfNeeded(opts, "start")) {
      this.status = STATUS.RUNNING;
    }
  }

  pause() {
    if (
      this.status === STATUS.RUNNING ||
      this.status === STATUS.COUNT_IN ||
      this.status === STATUS.READY_GATE
    ) {
      this._pausedFromGate = this.status === STATUS.READY_GATE;
      this.status = STATUS.PAUSED;
    }
  }

  resume(opts) {
    if (this.status !== STATUS.PAUSED) return;
    if (this._pausedFromGate) {
      // Resume the Ready gate count-up where it left off.
      this._pausedFromGate = false;
      this.status = STATUS.READY_GATE;
      return;
    }
    if (this.countInRemaining > 0) {
      // Resuming mid-count-in — just continue counting.
      this.status = STATUS.COUNT_IN;
      return;
    }
    if (!this._armCountInIfNeeded(opts, "resume")) {
      this.status = STATUS.RUNNING;
    }
  }

  finish() {
    this.status = STATUS.FINISHED;
    this.countInRemaining = 0;
    this.countInTotal = 0;
  }

  /**
   * Keep going (#199): a finished session carries on in blocks added to the
   * end of its plan. Recompiles from the new tree and starts the first item of
   * the plan's child at `childIndex`, running, with no count-in.
   * @param {Object} nextPlan
   * @param {number} childIndex
   * @returns {boolean} false when the session isn't finished or the child
   *          has nothing to run.
   */
  extend(nextPlan, childIndex) {
    if (this.status !== STATUS.FINISHED || !nextPlan) return false;
    const { queue, totalDuration } = compileWorkoutPlan(nextPlan, {
      skipLastRest: this.skipLastRest,
      profile: this.profile,
      historyMap: this.historyMap,
    });
    const first = queue.findIndex(
      (it) => it.sourceTreePath?.[1] === childIndex && it.executable !== false,
    );
    if (first < 0) return false;
    this.workoutPlan = nextPlan;
    this.queue = queue;
    this.totalDuration = totalDuration;
    this.currentIndex = first;
    this.status = STATUS.RUNNING;
    this.countInRemaining = 0;
    this.countInTotal = 0;
    this.readyGateElapsed = 0;
    this._pausedFromGate = false;
    return true;
  }

  restoreState({
    currentIndex = 0,
    status = STATUS.READY,
    countInRemaining = 0,
    countInTotal = 0,
    readyGateElapsed = 0,
    faffTotal = 0,
  } = {}) {
    if (this.queue.length === 0) {
      this.finish();
      return;
    }

    /** @type {EngineStatus} */
    const requestedStatus = Object.values(STATUS).includes(status)
      ? /** @type {EngineStatus} */ (status)
      : STATUS.READY;
    const nextCountInRemaining = Math.max(
      0,
      Math.floor(Number(countInRemaining) || 0),
    );
    const nextCountInTotal = Math.max(
      nextCountInRemaining,
      Math.floor(Number(countInTotal) || 0),
    );

    let idx = this._clampIndex(
      Number.isFinite(currentIndex) ? currentIndex : 0,
    );
    // Guard against a snapshot whose index landed on a now non-executable
    // micro-transition (e.g. saved before this tagging shipped): seek forward
    // to the nearest executable phase.
    if (!this._isExecutable(idx)) {
      const seeked = this._seekExecutable(idx, 1);
      idx = seeked < 0 ? this._clampIndex(this._seekExecutable(idx, -1)) : seeked;
    }
    this.currentIndex = idx;

    this.faffTotal = Math.max(0, Math.floor(Number(faffTotal) || 0));
    this.readyGateElapsed = Math.max(
      0,
      Math.floor(Number(readyGateElapsed) || 0),
    );

    if (requestedStatus === STATUS.FINISHED) {
      this.finish();
      return;
    }

    this.status = requestedStatus;
    this.countInRemaining = nextCountInRemaining;
    this.countInTotal = nextCountInTotal;
  }

  // ---------------------------------------------------------------------------
  // Navigation — two distinct forward methods
  // ---------------------------------------------------------------------------

  // Natural auto-advance fired by the timer tick when a timed phase hits 0.
  // Never arms count-in regardless of the new target. Seeks past any
  // non-executable micro-transitions to the next real phase.
  autoAdvance() {
    const next = this._seekExecutable(this.currentIndex + 1, 1);
    if (next < 0) {
      this.finish();
      return;
    }
    this.currentIndex = next;
    this.status = STATUS.RUNNING;
    this.countInRemaining = 0;
    this.countInTotal = 0;
  }

  // Manual "next" button or rep-phase "done" button. Seeks past micro-
  // transitions, then either enters the Ready gate (rep→timed) or arms the
  // count-in per the usual rules.
  skipNext(opts) {
    const fromItem = this._currentItem();
    const next = this._seekExecutable(this.currentIndex + 1, 1);
    if (next < 0) {
      this.finish();
      return;
    }
    this.currentIndex = next;
    if (!this._enterGateOrArm(opts, "manualSkip", fromItem)) {
      this.status = STATUS.RUNNING;
      this.countInRemaining = 0;
      this.countInTotal = 0;
    }
  }

  skipPrevious() {
    // Step back to the previous executable phase, never landing on a hidden
    // micro-transition. Exits a Ready gate without banking faff.
    const prev = this._seekExecutable(this.currentIndex - 1, -1);
    this.currentIndex = prev < 0 ? this.currentIndex : prev;
    this.status = STATUS.RUNNING;
    this.countInRemaining = 0;
    this.countInTotal = 0;
    this.readyGateElapsed = 0;
    this._pausedFromGate = false;
  }

  // Jump to the next index whose composite roundKey fingerprint differs
  // from the current one. Because roundKey encodes every active loop
  // counter, the boundary is always the innermost repeating loop.
  skipRoundForward(opts) {
    const current = this._currentItem();
    if (!current) return;
    const rk = current.roundKey;
    const fromItem = current;
    for (let j = this.currentIndex + 1; j < this.queue.length; j++) {
      if (this.queue[j].roundKey !== rk) {
        const target = this._seekExecutable(j, 1);
        if (target < 0) break; // only non-executable items ahead
        this.currentIndex = target;
        if (!this._enterGateOrArm(opts, "manualSkip", fromItem)) {
          this.status = STATUS.RUNNING;
          this.countInRemaining = 0;
          this.countInTotal = 0;
        }
        return;
      }
    }
    // No boundary ahead — clamp to the final executable item.
    const last = this._seekExecutable(this.queue.length - 1, -1);
    this.currentIndex = last < 0 ? this.currentIndex : last;
    this.status = STATUS.RUNNING;
  }

  // Jump to the first leaf of the current round. If already at the start
  // of the current round, jump to the start of the previous round.
  skipRoundBack(opts) {
    const current = this._currentItem();
    if (!current) return;
    const rk = current.roundKey;
    const fromItem = current;

    // Find the first index in the current round.
    let roundStart = this.currentIndex;
    while (roundStart > 0 && this.queue[roundStart - 1].roundKey === rk) {
      roundStart -= 1;
    }

    if (this.currentIndex > roundStart) {
      // Mid-round — snap to the start of the current round.
      this.currentIndex = roundStart;
    } else if (roundStart > 0) {
      // Already at round start — find the start of the previous round.
      const prevRk = this.queue[roundStart - 1].roundKey;
      let prevStart = roundStart - 1;
      while (prevStart > 0 && this.queue[prevStart - 1].roundKey === prevRk) {
        prevStart -= 1;
      }
      this.currentIndex = prevStart;
    } else {
      // Already at the very start.
      this.currentIndex = 0;
    }

    // Never land on a non-executable micro-transition at the round start.
    const seeked = this._seekExecutable(this.currentIndex, 1);
    this.currentIndex =
      seeked < 0 ? this._clampIndex(this._seekExecutable(this.currentIndex, -1)) : seeked;

    if (!this._enterGateOrArm(opts, "manualSkip", fromItem)) {
      this.status = STATUS.RUNNING;
      this.countInRemaining = 0;
      this.countInTotal = 0;
    }
  }

  // ---------------------------------------------------------------------------
  // Count-in ticking
  // ---------------------------------------------------------------------------

  // Called by the hook once per second while status === "countIn".
  // Returns true when the count-in has completed and the phase should begin.
  tickCountIn() {
    if (this.status !== STATUS.COUNT_IN) return false;
    this.countInRemaining = Math.max(0, this.countInRemaining - 1);
    if (this.countInRemaining === 0) {
      this.status = STATUS.RUNNING;
      this.countInTotal = 0;
      return true;
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // Ready gate
  // ---------------------------------------------------------------------------

  // Called by the hook once per second while status === "readyGate". Counts up
  // the faff timer. Returns true when the cap is hit (the hook then reflects
  // the auto-pause). The gate banks its elapsed time into faffTotal on the cap
  // and on confirmReady.
  tickReadyGate() {
    if (this.status !== STATUS.READY_GATE) return false;
    this.readyGateElapsed += 1;
    if (this.readyGateElapsed >= READY_GATE_MAX_SECONDS) {
      this.faffTotal += this.readyGateElapsed;
      this.readyGateElapsed = 0;
      this._pausedFromGate = false; // resolve into a normal pause
      this.status = STATUS.PAUSED;
      return true;
    }
    return false;
  }

  // "Ready" tapped — bank the faff time and run the normal count-in (if any)
  // into the timed phase the gate is sitting on.
  confirmReady(opts) {
    if (this.status !== STATUS.READY_GATE) return;
    this.faffTotal += this.readyGateElapsed;
    this.readyGateElapsed = 0;
    this._pausedFromGate = false;
    if (!this._armCountInIfNeeded(opts, "manualSkip")) {
      this.status = STATUS.RUNNING;
      this.countInRemaining = 0;
      this.countInTotal = 0;
    }
  }

  // ---------------------------------------------------------------------------
  // State export
  // ---------------------------------------------------------------------------

  getCurrentState() {
    const item = this._currentItem();
    // Seek past non-executable transitions so "next up" reflects the real next
    // phase the user will see, never a hidden transition node (issue #22).
    const nextIdx = this._seekExecutable(this.currentIndex + 1, 1);
    const nextItem = nextIdx >= 0 ? this.queue[nextIdx] : null;
    return {
      status: this.status,
      activeNode: item?.node ?? null,
      activeItem: item,
      nextUpNode: nextItem?.node ?? null,
      nextUpItem: nextItem,
      // True when the next executable phase is timed (s/m), false for reps-based
      // (rep estimate has no countdown). Drives CoachView's decision to show
      // a transition-overlay countdown only when the upcoming phase is timed.
      nextUpIsTimed: nextItem?.isTimed ?? false,
      isLast: this.queue.length === 0 || nextIdx < 0,
      canGoBack: this.currentIndex > 0,
      countInRemaining: this.countInRemaining,
      countInTotal: this.countInTotal,
      isReadyGate: this.status === STATUS.READY_GATE,
      readyGateElapsed: this.readyGateElapsed,
      faffTotal: this.faffTotal,
      queueLength: this.queue.length,
      currentIndex: this.currentIndex,
      totalDuration: this.totalDuration,
    };
  }
}

export { STATUS as WORKOUT_ENGINE_STATUS };
